import * as React from "react"

import { cn } from "../../cn"
import { COVAL_MARK_PATHS, COVAL_MARK_VIEWBOX } from "../../covalMark"
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion"
import { buildMarkParticleField } from "./markParticles"

/** Edge length of the resolved mark. */
const MARK_SIZE = 51
/**
 * Edge length of the canvas: wide enough for the cloud to sweep in from, and
 * the same parity as MARK_SIZE so the inset that centres it over the mark stays
 * a whole number. A canvas on a half pixel is resampled, and the particles blur
 * just as they land on the crisp mark.
 */
const FIELD_SIZE = 169
const CLOUD_RADIUS = 71
/** Scaled with the mark's stroke area so density reads the same at any size. */
const PARTICLE_COUNT = 400
/**
 * The cloud carries nearly all of the assembly; the stages after it are the
 * tail that resolves the mark, and are deliberately kept short so the logo
 * does not sit around finished with the cloud still on screen behind it.
 */
const CONVERGE_MS = 780
/** How long the crisp mark takes to fade up over the landed cloud. */
const HANDOFF_MS = 120
/**
 * The cloud's own exit, which waits for the mark to be fully opaque over it.
 * Fading the two past each other instead would leave the shape part
 * transparent in both layers at once, and the logo visibly dips at the moment
 * it lands. Dropping the cloud outright is no better: particles sit a fraction
 * of a pixel proud of the stroke they sample, and losing that fringe in one
 * frame reads as a snap. So it fades, under a mark that already covers it.
 */
const CLOUD_FADE_MS = 90
/**
 * Slack between the fade ending and the canvas coming out of the tree. The
 * transition starts on the frame after the style lands, so a timer set to the
 * exact duration can unmount a cloud that is still faintly visible — which is
 * the snap the fade exists to avoid.
 */
const CLOUD_RETIRE_BUFFER_MS = 32
/** Particles resolved → the last of the cloud gone. */
const CLOUD_RETIRE_MS = HANDOFF_MS + CLOUD_FADE_MS + CLOUD_RETIRE_BUFFER_MS
/**
 * The mark starts shifting tone before the last of the cloud has gone, so the
 * colour change tracks the settle rather than queueing behind it.
 */
const TONE_SHIFT_DELAY_MS = 40
/** How long the settled mark takes to reach graphite. */
const TONE_SHIFT_MS = 110
const FADE_IN_MS = 100
/** How fast the loading screen itself fades up when it first mounts. */
const PAGE_FADE_MS = 250
/** Alpha is quantised so a frame draws in a handful of batched fills. */
const ALPHA_STEPS = 6
/**
 * Paper sits close in tone to the contrast surface it draws on, so unresolved
 * particles need more alpha than a high-contrast cloud would to stay legible.
 */
const MIN_ALPHA = 0.3
const SETTLE_WOBBLE = 9
const TAU = Math.PI * 2
/**
 * Particles are drawn small while they are still scattered and reach
 * PARTICLE_RADIUS_LANDED as they settle. The field is sampled against the
 * landed size, so the cloud comes to rest exactly filling the stroke.
 */
const PARTICLE_RADIUS_SCATTERED = 0.62
const PARTICLE_RADIUS_LANDED = 1.12
/**
 * Paper. The cloud and the mark it first settles into share this tone, so the
 * handoff reads as the particles landing rather than a colour change.
 */
const CLOUD_TONE = "var(--surface-2)"
/** Graphite: where the settled mark ends up and stays. */
const MARK_TONE = "var(--coval-text-muted)"

/**
 * `pending` is the first client paint, before the canvas has had a chance to
 * report whether it can run. Nothing is drawn in it — showing the mark there
 * would flash the finished logo ahead of the cloud that assembles it.
 */
type Phase = "pending" | "particles" | "resolved"

/**
 * Share of the settle driven by the accelerating term; the remainder is an
 * ordinary ease-out drift. Blending a *fixed* proportion of each keeps the
 * curve monotonic — a progress-weighted blend can run backwards mid-flight —
 * while leaving real velocity at the finish, so the cloud quickens into the
 * mark instead of creeping the last few pixels.
 */
const SETTLE_SNAP = 0.45

function easeSettle(value: number) {
  const drift = 1 - (1 - value) ** 3
  return drift * (1 - SETTLE_SNAP) + value ** 3 * SETTLE_SNAP
}

/**
 * Draws the mark as a swirling particle cloud that collapses onto the stroke.
 * Runs once: on the last frame the particles sit exactly on the mark, and the
 * component hands off to the static SVG rather than looping.
 */
function MarkParticleField({ onResolved }: { onResolved: () => void }) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null)

  React.useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas?.getContext("2d")
    if (!canvas || !context) {
      onResolved()
      return
    }

    const field = buildMarkParticleField({
      count: PARTICLE_COUNT,
      markSize: MARK_SIZE,
      cloudRadius: CLOUD_RADIUS,
      particleRadius: PARTICLE_RADIUS_LANDED,
    })
    if (!field) {
      onResolved()
      return
    }

    const devicePixelRatio = window.devicePixelRatio || 1
    canvas.width = Math.round(FIELD_SIZE * devicePixelRatio)
    canvas.height = Math.round(FIELD_SIZE * devicePixelRatio)
    context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0)
    context.fillStyle = getComputedStyle(canvas).color || "currentColor"

    const { count } = field
    const centre = FIELD_SIZE / 2
    const batchX = Array.from(
      { length: ALPHA_STEPS },
      () => new Float32Array(count),
    )
    const batchY = Array.from(
      { length: ALPHA_STEPS },
      () => new Float32Array(count),
    )
    const batchRadius = Array.from(
      { length: ALPHA_STEPS },
      () => new Float32Array(count),
    )
    const batchSize = new Int32Array(ALPHA_STEPS)

    let animationFrame = 0
    let startedAt = 0

    const frame = (timestamp: number) => {
      if (!startedAt) {
        startedAt = timestamp
      }
      const elapsed = timestamp - startedAt
      const progress = Math.min(1, elapsed / CONVERGE_MS)
      const entry = Math.min(1, elapsed / FADE_IN_MS)

      batchSize.fill(0)
      context.clearRect(0, 0, FIELD_SIZE, FIELD_SIZE)

      for (let index = 0; index < count; index += 1) {
        const delay = field.delay[index]
        const homing = easeSettle(
          Math.min(1, Math.max(0, (progress - delay) / (1 - delay))),
        )
        const chaos = (1 - homing) ** 2

        const angle = field.orbitAngle[index] + elapsed * field.orbitSpin[index]
        const radius = field.orbitRadius[index]
        const cloudX = Math.cos(angle) * radius
        const cloudY = Math.sin(angle) * radius
        const wobble = chaos * SETTLE_WOBBLE
        const phase = field.phase[index]

        const x =
          centre +
          cloudX +
          (field.targetX[index] - cloudX) * homing +
          Math.sin(elapsed * 0.0032 + phase) * wobble
        const y =
          centre +
          cloudY +
          (field.targetY[index] - cloudY) * homing +
          Math.cos(elapsed * 0.0027 + phase * 1.4) * wobble

        const batch = Math.min(
          ALPHA_STEPS - 1,
          Math.floor(homing * ALPHA_STEPS),
        )
        const slot = batchSize[batch]
        batchX[batch][slot] = x
        batchY[batch][slot] = y
        batchRadius[batch][slot] =
          PARTICLE_RADIUS_SCATTERED +
          homing * (PARTICLE_RADIUS_LANDED - PARTICLE_RADIUS_SCATTERED)
        batchSize[batch] = slot + 1
      }

      for (let batch = 0; batch < ALPHA_STEPS; batch += 1) {
        const size = batchSize[batch]
        if (size === 0) {
          continue
        }
        const weight = (batch + 0.5) / ALPHA_STEPS
        context.globalAlpha = entry * (MIN_ALPHA + weight * (1 - MIN_ALPHA))
        context.beginPath()
        for (let slot = 0; slot < size; slot += 1) {
          const x = batchX[batch][slot]
          const y = batchY[batch][slot]
          const radius = batchRadius[batch][slot]
          context.moveTo(x + radius, y)
          context.arc(x, y, radius, 0, TAU)
        }
        context.fill()
      }

      if (progress >= 1) {
        onResolved()
        return
      }
      animationFrame = requestAnimationFrame(frame)
    }

    animationFrame = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(animationFrame)
  }, [onResolved])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="absolute inset-0"
      style={{ width: FIELD_SIZE, height: FIELD_SIZE, color: CLOUD_TONE }}
    />
  )
}

/**
 * The mark the cloud settles onto: a flat silhouette in the current colour,
 * with nothing moving in it. Drawn through a mask rather than as three stroked
 * paths so the strokes read as one shape — overlapping strokes would show
 * their joints while the mark is part way through a fade.
 */
function SettledMark({
  label,
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & { label: string }) {
  const maskId = `${React.useId()}-mark`

  return (
    <span role="status" aria-label={label} {...props}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${COVAL_MARK_VIEWBOX} ${COVAL_MARK_VIEWBOX}`}
        className="size-full"
        aria-hidden="true"
      >
        <defs>
          <mask id={maskId}>
            <g
              fill="none"
              stroke="#fff"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {COVAL_MARK_PATHS.map((path) => (
                <path key={path.d} strokeWidth={path.strokeWidth} d={path.d} />
              ))}
            </g>
          </mask>
        </defs>
        <rect
          width={COVAL_MARK_VIEWBOX}
          height={COVAL_MARK_VIEWBOX}
          fill="currentColor"
          mask={`url(#${maskId})`}
        />
      </svg>
      <span className="sr-only">{label}</span>
    </span>
  )
}

export interface LoadingScreenProps extends React.HTMLAttributes<HTMLDivElement> {
  label?: string
  /** Optional supporting copy rendered beneath the mark. */
  children?: React.ReactNode
}

/**
 * The full-viewport screen shown while the app resolves what to render. It
 * sits on the sidebar's surface so the first paint already belongs to the
 * product, and the mark assembles itself from a particle cloud, settles onto
 * its own tone, and then holds there, still, for as long as the wait lasts.
 */
const LoadingScreen = React.forwardRef<HTMLDivElement, LoadingScreenProps>(
  ({ children, className, label = "Loading", style, ...props }, ref) => {
    const prefersReducedMotion = usePrefersReducedMotion()
    const [phase, setPhase] = React.useState<Phase>("pending")

    // The canvas also resolves immediately when it cannot run at all, which is
    // what puts the mark on screen for browsers without path measurement.
    const handleResolved = React.useCallback(() => setPhase("resolved"), [])

    React.useEffect(() => {
      if (prefersReducedMotion) {
        setPhase("resolved")
        return
      }
      setPhase((current) => (current === "pending" ? "particles" : current))
    }, [prefersReducedMotion])

    // Derived rather than stateful so the canvas mounts in the same commit
    // that starts the cloud, with no blank render in between.
    const [fieldRetired, setFieldRetired] = React.useState(false)
    const fieldMounted =
      !prefersReducedMotion && !fieldRetired && phase !== "pending"

    // Once the mark has taken over it shifts off the cloud's tone onto its own.
    // With no cloud to match, reduced motion starts on the final tone.
    const [toned, setToned] = React.useState(false)

    // Keep the canvas alive through the crossfade, then drop it for good and
    // let the settled mark shift tone.
    React.useEffect(() => {
      if (phase !== "resolved") {
        return
      }
      if (prefersReducedMotion) {
        setFieldRetired(true)
        setToned(true)
        return
      }

      const toneTimer = window.setTimeout(
        () => setToned(true),
        TONE_SHIFT_DELAY_MS,
      )
      const retireTimer = window.setTimeout(
        () => setFieldRetired(true),
        CLOUD_RETIRE_MS,
      )
      return () => {
        window.clearTimeout(toneTimer)
        window.clearTimeout(retireTimer)
      }
    }, [phase, prefersReducedMotion])

    const markVisible = phase === "resolved"

    return (
      <div
        ref={ref}
        className={cn(
          "flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-surface-contrast",
          // A quick fade so the screen arrives rather than snapping in.
          !prefersReducedMotion && "animate-in fade-in-0",
          className,
        )}
        style={{ animationDuration: `${PAGE_FADE_MS}ms`, ...style }}
        {...props}
      >
        <div
          className="relative flex items-center justify-center"
          style={{ width: MARK_SIZE, height: MARK_SIZE }}
        >
          {/* The cloud needs far more room than the mark, so it overlays the
              layout box rather than widening it. It holds full strength while
              the mark fades up over the top of it, and only starts going once
              the mark is opaque enough to have covered it. */}
          {fieldMounted ? (
            <span
              aria-hidden="true"
              className={cn(
                "pointer-events-none absolute transition-opacity ease-out",
                phase === "resolved" ? "opacity-0" : "opacity-100",
              )}
              style={{
                inset: (MARK_SIZE - FIELD_SIZE) / 2,
                transitionDelay: `${phase === "resolved" ? HANDOFF_MS : 0}ms`,
                transitionDuration: `${phase === "resolved" ? CLOUD_FADE_MS : 0}ms`,
              }}
            >
              <MarkParticleField onResolved={handleResolved} />
            </span>
          ) : null}
          {/* Sized from MARK_SIZE, so the particle targets and the rendered
              mark can never drift apart. It fades up in the cloud's tone as the
              particles land, then shifts to graphite and stops — the wait is
              announced by the status role rather than by anything moving. One
              element with a transitioned `color` rather than a crossfade
              between two: the tone is a plain colour on the element, which
              interpolates, unlike a var() feeding an SVG stop-color. */}
          <SettledMark
            label={label}
            className={cn(
              "ease-out",
              markVisible ? "opacity-100" : "opacity-0",
            )}
            style={{
              width: MARK_SIZE,
              height: MARK_SIZE,
              color: toned ? MARK_TONE : CLOUD_TONE,
              transitionDuration: `${TONE_SHIFT_MS}ms, ${markVisible ? HANDOFF_MS : 0}ms`,
              transitionProperty: "color, opacity",
            }}
          />
        </div>
        {children}
      </div>
    )
  },
)

LoadingScreen.displayName = "LoadingScreen"

export { LoadingScreen }
